import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-timeinput/src/voy-timeinput';
import './voy-tide-heights-table';
import { VoyTideGraph } from '../src/voy-tide-graph'
import { VoyTimeinput } from '../../voy-timeinput/src/voy-timeinput';
import { VoyPortSearch } from '../src/voy-port-search'
import '../src/voy-port-search'
import { TidesModel } from '../../../src/models/tides-model';
import { Prediction } from '../../../src/services/tides-service';
import { VoyTidePdfExport, PdfExportParameters } from './voy-tide-pdf-export';
import { VoyTidePdfGraph } from '../src/voy-tide-pdf-graph';
import '../src/voy-tide-pdf-graph';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { TerminalInfo } from '../../../src/models/types/terminals-model.types';
import { ScheduleEntry, SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';

const deltaOneDay = 1000*60*60*24;

export interface TimeZone {
    label: string;
    offset: number;
}

@customElement('voy-tide-info-popup')
export class VoyTideInfoPopup extends LitElement
{
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Object}) ports: (TerminalInfo | null)[] = [];
    @property({type: Object}) selectedPort: (TerminalInfo | null) = null;
    @property({type: Number}) fromDate: number = -1;
    @property({type: Number}) toDate: number = -1;
    @property({type: Number}) minHeight: number = -1;
    @property({type: Object}) gsisEntry: ScheduleEntry|null = null;

    @property({attribute: false}) private _graphOrHeights: string = "graph";
    @property({attribute: false}) private _resolution: number = 60;
    @property({attribute: false}) timeZone: TimeZone = { label: "UTC 0", offset: 0 };
    
    private _heightsData: Prediction[] = [];
    private tidePdfGraph: VoyTidePdfGraph | null = null;
    private _tideData: number[][] = [];
    private _resolutionInput: HTMLInputElement | null = null;
    private _UTCTimeZones: Map<string, TimeZone> = new Map([
        [ "UTC -12", { label: "UTC -12", offset: -12*60*60*1000 }],
        [ "UTC -11", { label: "UTC -11", offset: -11*60*60*1000 }],
        [ "UTC -10", { label: "UTC -10", offset: -10*60*60*1000 }],
        [ "UTC -9:30", { label: "UTC -9:30", offset: -9.5*60*60*1000 }],
        [ "UTC -9", { label: "UTC -9", offset: -9*60*60*1000 }],
        [ "UTC -8", { label: "UTC -8", offset: -8*60*60*1000 }],
        [ "UTC -7", { label: "UTC -7", offset: -7*60*60*1000 }],
        [ "UTC -6", { label: "UTC -6", offset: -6*60*60*1000 }],
        [ "UTC -5", { label: "UTC -5", offset: -5*60*60*1000 }],
        [ "UTC -4", { label: "UTC -4", offset: -4*60*60*1000 }],
        [ "UTC -3:30", { label: "UTC -3:30", offset: -3.5*60*60*1000 }],
        [ "UTC -3", { label: "UTC -3", offset: -3*60*60*1000 }],
        [ "UTC -2", { label: "UTC -2", offset: -2*60*60*1000 }],
        [ "UTC -1", { label: "UTC -1", offset: -1*60*60*1000 }],
        [ "UTC 0", { label: "UTC 0", offset: 0*60*60*1000 }],
        [ "UTC +1", { label: "UTC +1", offset: 1*60*60*1000 }],
        [ "UTC +2", { label: "UTC +2", offset: 2*60*60*1000 }],
        [ "UTC +3", { label: "UTC +3", offset: 3*60*60*1000 }],
        [ "UTC +3:30", { label: "UTC +3:30", offset: 3.5*60*60*1000 }],
        [ "UTC +4", { label: "UTC +4", offset: 4*60*60*1000 }],
        [ "UTC +4:30", { label: "UTC +4:30", offset: 4.5*60*60*1000 }],
        [ "UTC +5", { label: "UTC +5", offset: 5*60*60*1000 }],
        [ "UTC +5:30", { label: "UTC +5:30", offset: 5.5*60*60*1000 }],
        [ "UTC +5:45", { label: "UTC +5:45", offset: 5.75*60*60*1000 }],
        [ "UTC +6", { label: "UTC +6", offset: 6*60*60*1000 }],
        [ "UTC +6:30", { label: "UTC +6:30", offset: 6*60*60*1000 }],
        [ "UTC +7", { label: "UTC +7", offset: 7*60*60*1000 }],
        [ "UTC +8", { label: "UTC +8", offset: 8*60*60*1000 }],
        [ "UTC +8:45", { label: "UTC +8:45", offset: 8.75*60*60*1000 }],
        [ "UTC +9", { label: "UTC +9", offset: 9*60*60*1000 }],
        [ "UTC +9:30", { label: "UTC +9:30", offset: 9.30*60*60*1000 }],
        [ "UTC +10", { label: "UTC +10", offset: 10*60*60*1000 }],
        [ "UTC +10:30", { label: "UTC +10:30", offset: 10.5*60*60*1000 }],
        [ "UTC +11", { label: "UTC +11", offset: 11*60*60*1000 }],
        [ "UTC +12", { label: "UTC +12", offset: 12*60*60*1000 }],
        [ "UTC +12:45", { label: "UTC +12:45", offset: 12.75*60*60*1000 }],
        [ "UTC +13", { label: "UTC +13", offset: 13*60*60*1000 }],
        [ "UTC +14", { label: "UTC +14", offset: 14*60*60*1000 }],
    ]);

    private _UTCTimeZonesReversed: Map<number, TimeZone> = new Map();

    get resolution() {
        return this._resolution;
    }
    
    constructor() {
        super();
        this._graphOrHeightsChanged = this._graphOrHeightsChanged.bind(this);
        this._exportPdf = this._exportPdf.bind(this);
        this.updateData = this.updateData.bind(this);

        [...this._UTCTimeZones.values()].forEach(t => this._UTCTimeZonesReversed.set(t.offset, t));
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
            }

            .container {
                display: grid;
                grid-template-rows: 120px 60px 1fr;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-03);
            }

            .port-and-dates {
                height: 120px;
                border-bottom: 1px solid var(--neutrals-grey-06, grey);
                display: grid;
                grid-template-columns: auto auto auto auto 1fr;
                grid-column-gap: 25px;
                align-items: center;
            }

            .graph-or-heights {
                height: 60px;
                border-bottom: 1px solid var(--neutrals-grey-06, grey);
                display: grid;
                align-items: center;
                grid-template-columns: auto 1fr auto auto;
                grid-gap: 15px;
            }

            .graph-radio {
                padding: 15px;
                padding-top: 20px;
                padding-bottom: 20px;
            }

            .graph-radio, .graph-radio > * {
                cursor: pointer;
            }

            .main-chart {
                height: 50vh;
                width: 80vw;
                margin-bottom: 5px;
                margin-left: 5px;
                margin-right: 5px;
            }

            .heights-table {
                height: 50vh;
                width: 80vw;
                overflow: auto;
                display: block;
                overflow-x: auto;
            }

            .heights-legend {
                margin-top: 15px;
                margin-bottom: 15px;
                display: grid;
                place-items: center;
            }

            .port {
                padding-left: 15px;
                display: grid;
                grid-auto-rows: auto auto;
                
                width: 230px;
            }

            voy-port-search {
                margin-top: 8px;
            }

            voy-timeinput[invalid] {
                border-color: var(--voyage-error-red, red);
            }

            .min-height {
                display: grid;
                grid-template-columns: auto auto;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02);
                grid-column-gap: 10px;   
                place-items: center;
                grid-column: 3;
            }

            .min-height-input {
                font-family: MaerskText;
                font-size: 14px;
                box-sizing: border-box;
                padding-left: 10px;
                padding-right: 10px;
                outline: none;
                color: var(--neutrals-grey-02);
                width: 80px;
                height: 32px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 2.5px;
            }

            .min-height-input:focus {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            .min-height-input:invalid {
                border: 1px solid var(--voyage-error-red, red);
            }

            .resolution-buttons {
                grid-column: 4;
            }

            .resolution {
                background: var(--neutrals-grey-10);
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 2px 0px 0px 2px;
                position: relative;
                padding: 5px;
                margin-right: -1px;
            }

            .resolution.button {
                width: 56px;
                height: 32px;
            }

            .resolution.button:hover {
                background: var(--neutrals-grey-09);
            }

            .resolution.input {
                width: 100px;
                height: 32px;
                margin-right: 10px;
                margin-left: 10px;
                padding: 0px 5px 0px 5px;
                cursor: auto;
                outline: none;
            }

            .resolution.input:focus {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            .resolution.input[error=true] {
                border: 1px solid var(--voyage-error-red, red);
            }

            .export-button {
                grid-column: 5;
                width: 135px;
                height: 32px;
                margin-right: 15px;

                background: var(--neutrals-grey-10);
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 2.5px;
            }

            .export-button:hover {
                background: var(--neutrals-grey-09);
            }

            .pdfChart {
                left: -2000px;
                position: absolute;
            }

            #dropdown {
                margin-top: 8px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
                height: 51px;
                padding: 12px;
                text-align: left;
                user-select: none;
            }

            #dropdown > option {
                height: 32px;
            }

            .time-offset {
                display: grid;
                grid-auto-rows: auto auto;
                width: 105px;
            }
        `
        ];
    }

    render()
    {
        return html`
        <voyage-popup accept-button-text="Close" .shown=${this.shown}
                      @close=${() => this.dispatchEvent(new Event('close'))}
                      @accept=${() => this.dispatchEvent(new Event('close'))}
                      ?disable-accept-button=${this.fromDate >= this.toDate}
                      .headerText=${"Tide"}
                      reject-button-text=${""}>
                      <div class="pdfChart">
                        <voy-tide-pdf-graph id="pdfgraph" .timeZone=${this.timeZone} .tideData=${this._tideData} .minHeight=${this.minHeight}></voy-tide-pdf-graph>
                      </div>
                      <div class="container">
                        <div class="port-and-dates">
                            <div class="port">
                                Port
                                <voy-port-search .terminalData=${TerminalsModel!.terminals} .fixedPorts=${this.ports} @port-selected=${this.onPortSelected}></voy-port-search>
                            </div>
                            <div class="date">
                                <voy-timeinput 
                                    id="fromDate" 
                                    custom-label="From... (Date/Time)"
                                    ?invalid=${this.fromDate >= this.toDate}
                                    @input=${(evt:InputEvent) => { this.fromDate = (<VoyTimeinput>evt.target).value; this.updateData();}}>
                                </voy-timeinput>
                            </div>
                            <div class="date">
                                <voy-timeinput
                                    id="toDate" 
                                    custom-label="...To (Date/Time)"
                                    ?invalid=${this.fromDate >= this.toDate}
                                    @input=${(evt:InputEvent) => { this.toDate = (<VoyTimeinput>evt.target).value; this.updateData(); }}>
                                </voy-timeinput>
                            </div>
                            <div class="time-offset">
                                Timezone
                                <select id="time-zone-dropdown"
                                    .value=${this.timeZone ? this.timeZone.label : ""}
                                    @input=${(evt: any) => this._onTimeZoneChanged(evt)}>
                                    ${Array.from(this._UTCTimeZones.keys()).map((zone) => html`
                                    <option value=${zone} ?selected=${ zone === this.timeZone!.label}>${zone}</option>
                                    `)}
                                </select>
                            </div>
                        </div>
                        <div class="graph-or-heights">
                            <div class="graph-type">
                                <label for="show-graph" class="graph-radio">
                                    <input 
                                        id="show-graph" 
                                        name="graphOrHeights" 
                                        type="radio" value="graph" 
                                        @change=${this._graphOrHeightsChanged}/>
                                        Tide Graph
                                </label>
                                <label for="show-heights" class="graph-radio"> 
                                    <input 
                                        id="show-heights" 
                                        name="graphOrHeights" 
                                        type="radio" 
                                        value="heights" 
                                        @change=${this._graphOrHeightsChanged}/>
                                        Heights
                                </label>
                            </div>
                            <div class="min-height" ?hidden=${this._graphOrHeights !== "graph"}>
                                <div ?hidden=${this._graphOrHeights !== "graph"}>Window of opportunity</div>
                                <div class="min-height-container" ?hidden=${this._graphOrHeights !== "graph"}>
                                    <input type="number" step="0.1" class="min-height-input" @input=${this._minHeightChanged}>
                                </div>
                            </div>
                            <div class="resolution-buttons" ?hidden=${this._graphOrHeights !== "heights"}>
                                <button class="resolution button" @click=${() => {this._resolutionInput!.value = "0:30"; this._resolutionInput!.dispatchEvent(new Event('change'));}}>30 min</button>
                                <button class="resolution button" @click=${() => {this._resolutionInput!.value = "1:00"; this._resolutionInput!.dispatchEvent(new Event('change'));}}>1 h</button>
                                <button class="resolution button" @click=${() => {this._resolutionInput!.value = "6:00"; this._resolutionInput!.dispatchEvent(new Event('change'));}}>6 h</button>
                                <button class="resolution button" @click=${() => {this._resolutionInput!.value = "12:00"; this._resolutionInput!.dispatchEvent(new Event('change'));}}>12 h</button>
                                <button class="resolution button" id="day-button" @click=${() => {this._resolutionInput!.value = "24:00"; this._resolutionInput!.dispatchEvent(new Event('change'));}}>24 h</button>
                                <input type="text" class="resolution input" id="resolution-input" @change=${this._onResolutionChanged} @click=${this._inputClick}>
                            </div>
                            <button class="export-button" @click=${() => this._exportPdf()}>Export to PDF</button>
                        </div>
                        ${this._graphOrHeights === "graph" ? 
                            html`
                                <div class="main-chart">
                                    <voy-tide-graph unit="Height in meters" id="graph" .timeZone=${this.timeZone} .tideData=${this._tideData} .minHeight=${this.minHeight}></voy-tide-graph>
                                </div>
                            ` :
                            html `
                                <div class="heights-table">
                                    <voy-tide-heights-table .timeZone=${this.timeZone} .data=${this._heightsData}></voy-tide-heights-table>
                                    <div class="heights-legend">in meters</div>
                                </div>
                            `
                        }
                      </div>
        </voyage-popup>
        `;
    }

    private _onTimeZoneChanged(evt: any) {
        const selectedTimeZone = this._UTCTimeZones.get((evt.target.value).toString()); 
        this._changeTimeZone(selectedTimeZone!);
    }

    private _changeTimeZone(newTimeZone: TimeZone) {
        const oldTimeZone = this.timeZone;

        if (this.fromDate > 0 && this.toDate > 0) {
            this.fromDate = this.fromDate - oldTimeZone.offset + newTimeZone.offset;
            this.toDate = this.toDate - oldTimeZone.offset + newTimeZone.offset;
        }

        this.timeZone = newTimeZone;

        this.updateData();
    }

    private _inputClick(evt: MouseEvent) {
        (<HTMLInputElement>evt.target).value = '';
    }

    private _onResolutionChanged(evt: any) {
        this._resolutionInput!.setAttribute("error", "false"); 

        const regexWithColon = /(^0*(?:[0-9][0-9]?|48)):([0-5][0-9])$/.exec(evt.target.value);
        if (regexWithColon) {
            const hh = regexWithColon[1];
            const mm = regexWithColon[2];
    
            this._resolution = parseInt(hh)*60 + parseInt(mm);    
            this.updateData();
            return;     
        }

        const numericInputWithHour = /^([0-9]*)([0-5][0-9])/.exec(evt.target.value);
        if (numericInputWithHour) {
            const hh = parseInt(numericInputWithHour[1]);
            const mm = parseInt(numericInputWithHour[2]);

            if (isNaN(hh) || hh <= 48) {
                this._resolution = (isNaN(hh) ? 0 : hh*60) + mm;    
                (<HTMLInputElement>evt.target).value = `${isNaN(hh) ? '0' : hh}:${mm.toString().padStart(2, "0")}`;
                this.updateData();
                return;
            }

            if (!isNaN(hh) && hh > 48) {
                this._resolutionInput!.setAttribute("error", "true");
                return;
            }
        }

        const singleMinute = /^([1-9])/.exec(evt.target.value);
        if (singleMinute) {
            const mm = parseInt(singleMinute[1]);

            this._resolution = mm;    
            (<HTMLInputElement>evt.target).value = `0:${mm.toString().padStart(2, "0")}`;
            this.updateData();
            return;
        }

        this._resolutionInput!.setAttribute("error", "true");
    }

    private _minHeightChanged(evt: { target: { valueAsNumber: number; }; }) {
        this.minHeight = evt.target.valueAsNumber;
    }

    private onPortSelected(evt: CustomEvent) {
        const terminal = evt.detail;
        this._changePort(terminal);
    }

    private _changePort(port: TerminalInfo) {
        this.selectedPort = port;

        const timezoneOffsetUTC = this._getOffsetBetweenTimezonesForDate(this.gsisEntry!.eta, this.selectedPort!.olsonTimezone!, 'Etc/GMT0');
        this._changeTimeZone(this._UTCTimeZonesReversed.get(timezoneOffsetUTC)!);

        if (this.gsisEntry!.arrivalTerminalCode === this.selectedPort!.locationId) {
            this._setDateRange(this.gsisEntry!.arrivalPortCall!.current);
        } 
        
        if (this.gsisEntry!.departureTerminalCode === this.selectedPort!.locationId) {
            this._setDateRange(this.gsisEntry!.departurePortCall!.current);
        } 

        this.updateData();
    }

    _exportPdf() {
        const parms : PdfExportParameters = {
            chart: this.tidePdfGraph!.chart,
            portName: this.selectedPort!.city,
            fromDate: this.fromDate,
            toDate: this.toDate,
            tideData: this._tideData,
            timeZone: this.timeZone
        }

        VoyTidePdfExport.Export(parms);
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has("gsisEntry")) {
            if (this.gsisEntry !== null && TerminalsModel!.terminals.size > 0) {
                const arrivalPort = TerminalsModel!.getTerminal(this.gsisEntry!.arrivalTerminalCode);
                const departurePort = TerminalsModel!.getTerminal(this.gsisEntry!.departureTerminalCode);

                if (arrivalPort && departurePort) {
                    this.ports = [arrivalPort, departurePort]; 
                    this._changePort(arrivalPort!);
                }
            }
        }

        if (changedProperties.has("fromDate")) {
            (<VoyTimeinput>this.shadowRoot!.getElementById("fromDate")).value = this.fromDate;
        }

        if (changedProperties.has("toDate")) {
            (<VoyTimeinput>this.shadowRoot!.getElementById("toDate")).value = this.toDate;
        }

        super.updated(changedProperties);
    }

    private _getOffsetBetweenTimezonesForDate(date: number, timezone1: string, timezone2: string): number {
        const timezone1Date = this._convertDateToAnotherTimeZone(date, timezone1);
        const timezone2Date = this._convertDateToAnotherTimeZone(date, timezone2);
        return timezone1Date.getTime() - timezone2Date.getTime();
      }
      
    private _convertDateToAnotherTimeZone(date: number, timezone: string) {
        const dateString = new Date(date).toLocaleString('en-US', {
            timeZone: timezone
        });
        return new Date(dateString);
    }

    private _setDateRange(portCall: SimplifiedPortCall){
        this.fromDate = new Date(portCall.arrivalAt).getTime() + this.timeZone.offset - deltaOneDay;
        this.toDate = new Date(portCall.departureAt).getTime() + this.timeZone.offset + deltaOneDay;
    }

    async updateData() {
        if (this.ports.length === 0) {
            return;
        }

        const station = await TidesModel!.getClosestStation(this.selectedPort!.locationId);
        if (!station) {
            console.warn("No tide station found for port " + this.selectedPort!.locationId + " at " + this.selectedPort!.latitude + ", " + this.selectedPort!.longitude );
            return;
        }
        const data = await TidesModel!.getPredictionsRange(station.id, (new Date(this.fromDate - this.timeZone.offset)).toISOString(), (new Date(this.toDate - this.timeZone.offset)).toISOString());
        const formattedData: number[][] = data.map(d => [Date.parse(d.DateTime), d.Height]);

        this._tideData = formattedData;
        this._heightsData = await TidesModel!.getPredictionsRangeWithInterval(station.id, (new Date(this.fromDate - this.timeZone.offset)).toISOString(), (new Date(this.toDate - this.timeZone.offset)).toISOString(), this._resolution);

        this.requestUpdate();
    }

    firstUpdated() {
        this.tidePdfGraph = <VoyTidePdfGraph>this.shadowRoot!.getElementById('pdfgraph');
        this._resolutionInput = <HTMLInputElement>this.shadowRoot!.getElementById('resolution-input');
        
        //set initial value on graph
        const graphRadio = <HTMLInputElement>this.shadowRoot!.getElementById("show-graph");
        graphRadio.checked = true;

        this._resolutionInput.value = "1:00";
    }

    private _graphOrHeightsChanged(evt: any) {
        this._graphOrHeights = evt.target.value; 
        this.updateData();
    }
};
